import { type FC, useState, useEffect, useCallback, useRef } from "react";
import { usePlaidLink, type PlaidLinkOnSuccess } from "react-plaid-link";
import { Link, Loader2, Pencil, Upload } from "lucide-react";
import { enqueueSnackbar } from "notistack";

import { Button } from "@/shared/components/ui/button";
import { usePlaid } from "@/shared/hooks/usePlaid";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUploadProcessing } from "@/shared/hooks/useUploadProcessing";
import { UploadDocumentModal } from "@/shared/components/overlay/UploadDocumentModal";
import { InstitutionLogos } from "@/shared/components/InstitutionLogos";
import { OptionCard } from "@/pages/postloss/modules/components/OptionCard";
import { DocumentProcessingOverlay } from "@/pages/postloss/modules/components/DocumentProcessingOverlay";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

export const AddAccounts: FC<FlowCustomComponentProps> = ({ goToScreen }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const firstName = person?.first_name ?? "their";

  const { createLinkToken, isCreatingLinkToken, exchangeToken, isExchanging } = usePlaid(estateId);

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const handlePlaidSuccess: PlaidLinkOnSuccess = useCallback(
    async (publicToken, metadata) => {
      try {
        await exchangeToken({
          publicToken,
          institution: {
            institution_id: metadata.institution?.institution_id ?? "",
            name: metadata.institution?.name ?? "Unknown Bank",
          },
        });
        goToScreen("review_accounts");
      } catch (err) {
        enqueueSnackbar("Couldn't connect account. Please try again.", {
          variant: "error",
        });
        sentryUtils.captureError(err, "Plaid exchange token failed", {
          estateId: person?.estate_id,
        });
      }
    },
    [exchangeToken, goToScreen],
  );

  const handlePlaidExit = useCallback(() => {
    setLinkToken(null);
  }, []);

  const { open: openPlaidLink, ready: plaidLinkReady } = usePlaidLink({
    token: linkToken,
    onSuccess: handlePlaidSuccess,
    onExit: handlePlaidExit,
  });

  useEffect(() => {
    if (linkToken && plaidLinkReady) {
      openPlaidLink();
    }
  }, [linkToken, plaidLinkReady, openPlaidLink]);

  const { isProcessing, startProcessing } = useUploadProcessing({
    onProcessed: useCallback(() => goToScreen("review_accounts"), [goToScreen]),
  });

  const handleUploadSuccess = useCallback(
    (documentIds: number[]) => {
      setShowUploadModal(false);
      startProcessing(documentIds);
    },
    [startProcessing],
  );

  const isOpeningPlaid = useRef(false);

  const openPlaid = async () => {
    if (isOpeningPlaid.current) return;
    isOpeningPlaid.current = true;
    try {
      const result = await createLinkToken(undefined);
      setLinkToken(result.link_token);
    } catch (err) {
      enqueueSnackbar("Couldn't connect your bank. Please try again.", {
        variant: "error",
      });
      sentryUtils.captureError(err, "Plaid link token creation failed", {
        estateId: person?.estate_id,
      });
    } finally {
      isOpeningPlaid.current = false;
    }
  };

  const isConnecting = isCreatingLinkToken || isExchanging;

  if (isProcessing) {
    return (
      <DocumentProcessingOverlay
        messages={[
          "Scanning your statement...",
          "Identifying account details...",
          "Organizing what we found...",
        ]}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-center">Let&apos;s add {firstName}&apos;s accounts</h2>
      <p className="text-center text-primary text-pretty">
        Pick what works best. You can always add more accounts later.
      </p>
      <OptionCard
        icon={Pencil}
        iconBg="bg-green-100"
        iconColor="text-green-800"
        title="Enter details yourself"
        subtitle="Add what you have. Even partial info helps."
        onClick={() => goToScreen("manual_entry")}
        disabled={isConnecting}
      />

      <OptionCard
        icon={Upload}
        iconBg="bg-blue-100"
        iconColor="text-blue-600"
        title="Upload a statement"
        subtitle="Share a PDF or image. We'll auto-populate the details."
        onClick={() => setShowUploadModal(true)}
        disabled={isConnecting}
      />

      <OptionCard
        icon={Link}
        iconBg="bg-orange-50"
        iconColor="text-orange-500"
        title="Connect directly"
        subtitle={`Securely link ${firstName}'s accounts. We'll auto-populate balances and details.`}
        onClick={openPlaid}
        disabled={isConnecting}
        footer={
          <div className="mt-1.5">
            <InstitutionLogos />
          </div>
        }
      />

      <Button
        variant="link"
        className="mx-auto text-secondary"
        onClick={() => goToScreen("review_accounts")}
      >
        Skip
      </Button>

      {isConnecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="flex items-center gap-3 rounded-lg bg-white p-6">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>{isExchanging ? "Connecting accounts..." : "Connecting to Plaid..."}</span>
          </div>
        </div>
      )}

      {estateId && (
        <UploadDocumentModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
          estateId={estateId}
          title="Upload a statement"
          subtitle="We'll review and auto-populate the details so you don't have to enter them yourself."
          tooltip="Bank statements, investment statements, credit card statements, or any financial account summary."
          securityNote="Files are encrypted and stored securely in your Documents."
        />
      )}
    </div>
  );
};
